import { ApiError } from '~/utils/errors';

// Pure API service for billing operations - no state management
export const billingService = {
    async updatePaymentMethod(paymentData: any): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi(
                nuxtApp.$api.routes.me.financial.cards.update(),
                {
                    method: 'PUT',
                    body: JSON.stringify(paymentData),
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to update payment method', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async confirmPaymentMethod(paymentData: { payment_intent_id: string }): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi(
                nuxtApp.$api.routes.me.financial.confirmPayment(),
                {
                    method: 'POST',
                    body: JSON.stringify(paymentData),
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to confirm payment method', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async removePaymentMethod(): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.me.financial.cards.remove(),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to remove payment method', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getInvoices(page: number = 1, perPage: number = 10): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.me.financial.invoices.get(page, perPage)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch invoices', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async downloadInvoice(invoiceId: string): Promise<Blob> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<Blob>(
                nuxtApp.$api.routes.me.financial.invoices.download(invoiceId),
                {
                    responseType: 'blob'
                }
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to download invoice', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getCreditBalance(): Promise<number> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.me.financial.creditBalance.get()
            );
            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch credit balance', status);
            }
            return data.data.credit_balance || 0;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async purchaseGiftCertificate(name: string, email: string, amount: number, paymentMethodId?: string): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const payload: any = {
                name,
                email,
                amount,
            };

            // Add payment method if provided
            if (paymentMethodId) {
                payload.payment_method = paymentMethodId;
            }

            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.me.financial.giftCertificates.purchase(),
                {
                    method: 'POST',
                    body: payload,
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to purchase gift certificate', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async applyGiftCertificate(code: string): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.me.financial.giftCertificates.apply(),
                {
                    method: 'POST',
                    body: {
                        code,
                    },
                }
            );

            if (status !== 201 || !data) {
                throw new ApiError(message || 'Failed to apply gift certificate', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async sendGiftCertificateEmail(recipientName: string, recipientEmail: string, code: string): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.me.financial.giftCertificates.email(code),
                {
                    method: 'POST',
                    body: {
                        recipientName,
                        recipientEmail,
                    },
                }
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to send gift certificate email', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },
}; 